import { useState, useCallback } from "react";
import { useDropzone, type FileWithPath } from "react-dropzone";
import { Button } from "@stackje/shared-components/ui/button";
import { Label } from "@stackje/shared-components/ui/label";
import clsx from "clsx";
import {
  ImageEditor,
  type FileWithPreview,
  type SelectedImageData,
} from "@/components/shared/ImageEditor";

interface FormImageUploaderProps {
  label?: string;
  currentImage: SelectedImageData | null;
  onImageChange: (imageData: SelectedImageData | null) => void;
  targetCropWidth?: number;
  targetCropHeight?: number;
  allowFreeCrop?: boolean;
  imagePreviewClassName?: string;
  dropzoneClassName?: string;
  outputFormat?: 'png' | 'jpg';
  disabled?: boolean;
  /** Builder assets keep their own upload endpoint and original bytes (for QR codes). */
  onFileUpload?: (file: File) => void;
  acceptedTypes?: string[];
  maxSize?: number;
}

export function FormImageUploader({
  label,
  currentImage,
  onImageChange,
  targetCropWidth = 800,
  targetCropHeight = 450,
  allowFreeCrop = false,
  imagePreviewClassName = "max-w-xs h-auto rounded border object-contain aspect-video bg-muted",
  dropzoneClassName = "p-6 border-2 border-dashed rounded-md transition-colors",
  outputFormat = 'jpg',
  disabled = false,
  onFileUpload,
  acceptedTypes,
  maxSize,
}: FormImageUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<FileWithPreview | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    if (disabled) return;
    const file = acceptedFiles[0];
    if (file) {
      if (onFileUpload) { onFileUpload(file); return; }
      const fileWithPreview: FileWithPreview = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setSelectedFile(fileWithPreview);
      setDialogOpen(true);
    }
  }, [disabled, onFileUpload]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: acceptedTypes ? Object.fromEntries(acceptedTypes.map((type) => [type, []])) : {
      "image/jpeg": [],
      "image/png": [],
      "image/bmp": [],
    },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled,
    maxSize,
  });

  const handleRemoveClick = () => {
    if (disabled) return;
    onImageChange(null);
    setSelectedFile(null);
  };

  // const handleEditExisting = () => {
  //   if (disabled) return;
  //   setSelectedFile(null);
  //   setDialogOpen(true);
  // };

  return (
    <div className="w-full">
      {label && <Label>{label}</Label>}
      <div className="mt-2 space-y-3">
        <div
          {...getRootProps({
            className: clsx(
              dropzoneClassName,
              isDragActive && !disabled && "border-primary bg-primary/10",
              disabled && "opacity-50 cursor-not-allowed"
            ),
          })}
        >
          <input {...getInputProps()} />
          {currentImage?.previewUrl ? (
            <div className="space-y-2">
              <img
                src={currentImage.previewUrl}
                alt="Current preview"
                className={clsx(imagePreviewClassName, "w-full")}
              />
              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={open}
                  disabled={disabled}
                >
                  Replace Image
                </Button>
                {/* <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleEditExisting}
                  disabled={disabled}
                >
                  Edit Image
                </Button> */}
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveClick}
                  disabled={disabled}
                >
                  Remove Image
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center">
              {isDragActive ? (
                <p className="text-primary">Drop the image here ...</p>
              ) : (
                <p className="text-sm text-muted-foreground mb-2">
                  Drag & drop an image here, or click the button below.
                </p>
              )}
              <Button type="button" variant="outline" size="sm" onClick={open} disabled={disabled}>
                Select Image
              </Button>
            </div>
          )}
        </div>
      </div>

      {!onFileUpload && <ImageEditor
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        initialFile={selectedFile}
        currentImage={currentImage}
        onEditComplete={(imageData) => {
          onImageChange(imageData);
          setSelectedFile(null);
          if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
          }
        }}
        onCancel={() => {
          if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
          }
          setSelectedFile(null);
        }}
        targetCropWidth={targetCropWidth}
        targetCropHeight={targetCropHeight}
        allowFreeCrop={allowFreeCrop}
        outputFormat={outputFormat}
      />}
    </div>
  );
}
